import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { KeyRound, Lock } from 'lucide-react';
import { connectAws, prepareAws, setKubernetesAccess, testAwsStateBackend } from '../lib/api';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Alert } from './ui/alert';
import { Toggle } from './ui/toggle';

/** The raw S3 template URL, pulled out of the quick-create console link (its params
 * live in the hash: #/stacks/quickcreate?templateURL=…). Used for manual setup. */
function templateUrlFromCfn(cloudFormationUrl: string): string {
  try {
    const hash = new URL(cloudFormationUrl).hash;
    const qi = hash.indexOf('?');
    return qi < 0 ? '' : (new URLSearchParams(hash.slice(qi + 1)).get('templateURL') ?? '');
  } catch {
    return '';
  }
}

/** A labelled read-only value with a copy button, matching the GCP/Azure modals. */
function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="rounded-sm border border-border bg-surface-2/40">
      <div className="flex items-center justify-between border-b border-border px-3 py-1.5">
        <span className="text-xs text-muted">{label}</span>
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            void navigator.clipboard.writeText(value).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            })
          }
        >
          {copied ? 'Copied ✓' : 'Copy'}
        </Button>
      </div>
      <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-all px-3 py-2 text-xs leading-relaxed text-bone">
        {value}
      </pre>
    </div>
  );
}

/**
 * Guided AWS connect (S6) — the cloud analogue of the GitHub install modal.
 * Fetches an ExternalId + CloudFormation quick-create link, walks the customer
 * through creating a read-only cross-account role, then completes the
 * connection with the role ARN (validated by assuming the role).
 */
export function AwsSetupModal({
  envId,
  onClose,
  onConnected,
}: {
  envId: string;
  onClose: () => void;
  onConnected: () => void;
}) {
  const [roleArn, setRoleArn] = useState('');
  const [regions, setRegions] = useState('');
  const [stateBucket, setStateBucket] = useState('');
  const [stateKey, setStateKey] = useState('');
  const [stateKms, setStateKms] = useState(false);
  const [stateKmsArn, setStateKmsArn] = useState('');
  const [includeK8s, setIncludeK8s] = useState(true);
  const [k8sAutoProvision, setK8sAutoProvision] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Optional S3 Terraform-state backend, if the customer fills it in.
  const stateBackend =
    stateBucket.trim() && stateKey.trim() && (!stateKms || stateKmsArn.trim())
      ? {
          bucket: stateBucket.trim(),
          key: stateKey.trim(),
          ...(stateKms && stateKmsArn.trim() ? { kmsKeyArn: stateKmsArn.trim() } : {}),
        }
      : undefined;

  const prep = useQuery({
    queryKey: ['aws-prepare', envId],
    queryFn: () => prepareAws(envId),
    staleTime: Infinity,
    retry: false,
  });

  // Values for the manual (non-one-click) setup path.
  const templateUrl = prep.data ? templateUrlFromCfn(prep.data.cloudFormationUrl) : '';
  const cliCreate = prep.data
    ? [
        'aws cloudformation create-stack \\',
        '  --stack-name lizrd-integration \\',
        `  --template-url ${templateUrl} \\`,
        `  --parameters ParameterKey=ExternalId,ParameterValue=${prep.data.externalId} ParameterKey=LizrdAccountId,ParameterValue=${prep.data.lizrdAccountId} \\`,
        '  --capabilities CAPABILITY_NAMED_IAM \\',
        '  --region us-east-1',
      ].join('\n')
    : '';
  const cliOutputs =
    "aws cloudformation describe-stacks --stack-name lizrd-integration \\\n  --region us-east-1 --query 'Stacks[0].Outputs'";

  const connect = useMutation({
    mutationFn: () =>
      connectAws(envId, {
        roleArn: roleArn.trim(),
        externalId: prep.data!.externalId,
        regions: regions.trim() ? regions.split(',').map((r) => r.trim()) : undefined,
        stateBackend,
      }),
    onSuccess: async (integration) => {
      if (integration.status === 'ERROR') {
        setError(
          integration.lastError ?? 'Could not assume the role. Check the setup and try again.',
        );
        return;
      }
      // Opt-in Kubernetes (EKS) pod-level discovery, mirroring domain settings.
      // Best-effort: never block the connection if enabling it hiccups.
      if (includeK8s) {
        try {
          await setKubernetesAccess(envId, integration.id, {
            enabled: true,
            autoProvision: k8sAutoProvision,
          });
        } catch {
          /* best-effort */
        }
      }
      onConnected();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Failed to connect'),
  });

  const testState = useMutation({
    mutationFn: () =>
      testAwsStateBackend(envId, {
        roleArn: roleArn.trim(),
        externalId: prep.data!.externalId,
        regions: regions.trim() ? regions.split(',').map((r) => r.trim()) : undefined,
        stateBackend: {
          bucket: stateBucket.trim(),
          key: stateKey.trim(),
          ...(stateKms && stateKmsArn.trim() ? { kmsKeyArn: stateKmsArn.trim() } : {}),
        },
      }),
  });

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4"
      onClick={onClose}
      role="presentation"
    >
      <Card
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <h2 className="font-display text-xl font-bold">Connect AWS</h2>
        <p className="mt-2 text-sm text-muted">
          Create a read-only IAM role in your AWS account that Lizrd can assume. You stay in control
          and can delete the role any time.
        </p>

        <ul className="mt-4 space-y-2 text-sm">
          <li className="flex gap-2">
            <Lock className="h-4 w-4" aria-hidden />
            <span>
              <strong className="text-bone">Read-only.</strong> Lizrd only describes your resources
              and reads cost/usage — it never modifies your infrastructure.
            </span>
          </li>
          <li className="flex gap-2">
            <KeyRound className="h-4 w-4" aria-hidden />
            <span>
              <strong className="text-bone">No stored secrets.</strong> Access is via a
              cross-account role with a unique ExternalId; short-lived credentials are minted on
              demand.
            </span>
          </li>
          <li className="flex gap-2">
            <span aria-hidden>↩️</span>
            <span>
              <strong className="text-bone">Revoke any time.</strong> Deleting the CloudFormation
              stack removes the role — access ends immediately, with nothing left behind.
            </span>
          </li>
        </ul>

        <details className="mt-4 rounded-sm border border-border bg-surface-2/40 px-3 py-2 text-sm">
          <summary className="cursor-pointer font-semibold text-bone">
            What is this CloudFormation stack, and why?
          </summary>
          <div className="mt-2 space-y-2 text-muted">
            <p>
              CloudFormation is just the one-click way to create a single{' '}
              <strong className="text-bone">read-only IAM role</strong> in your account — the same
              role you could build by hand, only without the chance to mis-scope it. Nothing runs in
              your account after setup.
            </p>
            <p>The template defines exactly one thing, with three guarantees baked in:</p>
            <ul className="list-disc space-y-1 pl-5">
              <li>
                <strong className="text-bone">Trust:</strong> only Lizrd’s AWS account can assume
                the role, and only when it presents your unique{' '}
                <span className="whitespace-nowrap">ExternalId</span> — so no one else can use it,
                even if they knew the ARN.
              </li>
              <li>
                <strong className="text-bone">Least privilege:</strong> the role can only{' '}
                <em>describe</em> resources and read cost/usage (EC2, EBS, RDS, CloudWatch, Cost
                Explorer). It cannot create, modify, or delete anything.
              </li>
              <li>
                <strong className="text-bone">No keys:</strong> Lizrd never receives long-lived
                credentials. To sync, it assumes the role to mint credentials that expire in an
                hour, then discards them.
              </li>
            </ul>
            <p>
              You keep full control: the stack is an auditable record of exactly what you granted,
              and deleting it removes the role completely.
            </p>
          </div>
        </details>

        {/* Advanced, out of the critical path (audit F8): EKS + IaC setup is
            collapsed so the core flow is just launch-stack → paste ARN → connect.
            Toggles keep their recommended defaults when expanded. */}
        <details className="mt-4 rounded-md border border-iris/30 bg-iris-dim/20 p-4">
          <summary className="cursor-pointer text-sm font-semibold text-bone">
            Advanced: Kubernetes &amp; IaC (optional)
          </summary>
          <p className="mt-1 text-xs leading-relaxed text-muted">
            Only needed if you run EKS or want exact code↔cloud correlation — not required to
            connect. Recommended defaults are on.
          </p>
          <div className="mt-3 flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-bone">Include Kubernetes workloads</p>
              <p className="mt-0.5 text-xs leading-relaxed text-muted">
                If you run Kubernetes (EKS), Lizrd can surface deep, in-cluster optimizations —
                right-size pods and spot idle workloads — but only if you let it read and sync your
                cluster data. Read-only, like everything else.
              </p>
            </div>
            <Toggle
              checked={includeK8s}
              onChange={setIncludeK8s}
              label="Include Kubernetes workloads"
            />
          </div>
          {includeK8s && (
            <div className="mt-3 flex items-start justify-between gap-3 border-t border-hairline pt-3">
              <div>
                <p className="text-sm font-medium text-bone">Set up cluster access automatically</p>
                <p className="mt-0.5 text-xs leading-relaxed text-muted">
                  {k8sAutoProvision ? (
                    <>
                      Lizrd manages <span className="text-bone">only its own</span> read-only EKS
                      Access Entry (via the AWS-managed view policy — no <code>kubectl</code>). The
                      CloudFormation template grants a tightly-scoped permission for this, which it
                      can use on no identity but its own.
                    </>
                  ) : (
                    <>
                      You’ll grant the read-only access yourself. After connecting, the
                      integration’s settings show the exact copy-paste commands — discovery reads
                      once it’s applied.
                    </>
                  )}
                </p>
              </div>
              <Toggle
                checked={k8sAutoProvision}
                onChange={setK8sAutoProvision}
                label="Set up cluster access automatically"
              />
            </div>
          )}
          <p className="mt-3 border-t border-hairline pt-3 text-xs leading-relaxed text-muted">
            <span className="font-medium text-bone">Infrastructure as Code.</span> Lizrd reads your
            Terraform/IaC from your connected code repository to tie each recommendation to the
            exact file to change — you can point it at your Terraform state for exact code↔cloud
            correlation below, or anytime in the domain’s settings.
          </p>
        </details>

        {prep.isError && (
          <Alert variant="danger" className="mt-4">
            AWS onboarding isn’t configured on this server.
          </Alert>
        )}

        {prep.data && (
          <div className="mt-5 space-y-4">
            <ol className="list-decimal space-y-3 pl-5 text-sm">
              <li>
                Launch CloudFormation in your AWS account — it prefills your ExternalId and Lizrd’s
                account id.
                <p className="mt-1 text-xs text-muted">
                  Tip: sign in to the AWS account you want to connect first (in another tab) — then
                  the button opens the stack ready to create, with no account-switching mid-flow.
                </p>
                <div className="mt-2">
                  <a href={prep.data.cloudFormationUrl} target="_blank" rel="noopener noreferrer">
                    <Button size="sm">Launch CloudFormation ↗</Button>
                  </a>
                </div>
                <p className="mt-2 text-xs text-muted">
                  ExternalId (already in the link):{' '}
                  <code className="rounded-sm bg-surface-2 px-1">{prep.data.externalId}</code>
                </p>
                <details className="mt-3 rounded-sm border border-border bg-surface-2/40 px-3 py-2 text-sm">
                  <summary className="cursor-pointer font-semibold text-bone">
                    Prefer to set it up manually?
                  </summary>
                  <div className="mt-3 space-y-3 text-muted">
                    <p>
                      The same read-only role, created by hand — in the AWS Console or with the CLI.
                      These are your values (the one-click button uses them too).
                    </p>
                    <CopyField label="CloudFormation template URL" value={templateUrl} />
                    <div className="grid grid-cols-2 gap-2">
                      <CopyField label="ExternalId" value={prep.data.externalId} />
                      <CopyField label="Lizrd account id" value={prep.data.lizrdAccountId} />
                    </div>
                    <div>
                      <p className="font-medium text-bone">In the AWS Console</p>
                      <ol className="mt-1 list-decimal space-y-1 pl-5">
                        <li>
                          CloudFormation →{' '}
                          <strong className="text-bone">Create stack → With new resources</strong>.
                        </li>
                        <li>
                          Template source → <strong className="text-bone">Amazon S3 URL</strong> →
                          paste the template URL above → Next.
                        </li>
                        <li>
                          Stack name{' '}
                          <code className="rounded-sm bg-surface-2 px-1">lizrd-integration</code>;
                          set the <strong className="text-bone">ExternalId</strong> and{' '}
                          <strong className="text-bone">LizrdAccountId</strong> parameters from
                          above → Next → Next.
                        </li>
                        <li>
                          Check{' '}
                          <strong className="text-bone">
                            “I acknowledge that AWS CloudFormation might create IAM resources”
                          </strong>{' '}
                          → <strong className="text-bone">Create stack</strong>.
                        </li>
                        <li>
                          When it reads{' '}
                          <code className="rounded-sm bg-surface-2 px-1">CREATE_COMPLETE</code>,
                          open <strong className="text-bone">Outputs</strong>, copy the role ARN,
                          and paste it in step 2 below.
                        </li>
                      </ol>
                    </div>
                    <div>
                      <p className="font-medium text-bone">Or with the AWS CLI</p>
                      <div className="mt-1 space-y-2">
                        <CopyField label="1 · create the role" value={cliCreate} />
                        <CopyField label="2 · read the role ARN from Outputs" value={cliOutputs} />
                      </div>
                    </div>
                  </div>
                </details>
              </li>
              <li>
                After the stack completes, copy the role ARN from its <em>Outputs</em> and paste it
                here.
                <label className="mt-2 block">
                  <Input
                    placeholder="arn:aws:iam::123456789012:role/lizrd-integration-…"
                    value={roleArn}
                    onChange={(e) => setRoleArn(e.target.value)}
                  />
                </label>
                <label className="mt-2 block">
                  <span className="mb-1 block text-xs text-muted">
                    Regions (optional, comma-separated — blank scans all enabled)
                  </span>
                  <Input
                    placeholder="us-east-1, eu-west-1"
                    value={regions}
                    onChange={(e) => setRegions(e.target.value)}
                  />
                </label>
              </li>
            </ol>

            <details className="rounded-sm border border-border bg-surface-2/40 px-3 py-2 text-sm">
              <summary className="cursor-pointer font-semibold text-bone">
                Advanced (optional): Terraform state
              </summary>
              <div className="mt-3 space-y-2">
                <p className="text-xs text-muted">
                  Point Lizrd at your <code>terraform.tfstate</code> in S3 for exact code↔cloud
                  correlation. Read-only; the state is never stored. You can also set this later in
                  the domain’s settings.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <label className="block">
                    <span className="mb-1 block text-xs text-muted">State S3 bucket</span>
                    <Input
                      placeholder="acme-tfstate-123456789012"
                      value={stateBucket}
                      onChange={(e) => setStateBucket(e.target.value)}
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs text-muted">…key / path</span>
                    <Input
                      placeholder="prod/terraform.tfstate"
                      value={stateKey}
                      onChange={(e) => setStateKey(e.target.value)}
                    />
                  </label>
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <Toggle
                    checked={stateKms}
                    onChange={setStateKms}
                    label="State is KMS-encrypted"
                  />
                  <span className="text-xs text-haze">My state is KMS-encrypted (SSE-KMS)</span>
                </div>
                {stateKms && (
                  <label className="block">
                    <span className="mb-1 block text-xs text-muted">KMS key ARN (required)</span>
                    <Input
                      placeholder="arn:aws:kms:us-east-1:…:key/…"
                      value={stateKmsArn}
                      onChange={(e) => setStateKmsArn(e.target.value)}
                    />
                  </label>
                )}
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!stateBackend || testState.isPending}
                    onClick={() => testState.mutate()}
                  >
                    {testState.isPending ? 'Testing…' : 'Test read'}
                  </Button>
                  {testState.data &&
                    (testState.data.ok ? (
                      <span className="text-xs text-iris">
                        ✓ Read {testState.data.resourceCount} resources from state
                      </span>
                    ) : (
                      <span className="text-xs text-danger">{testState.data.message}</span>
                    ))}
                  {testState.isError && (
                    <span className="text-xs text-danger">Test failed — check the values.</span>
                  )}
                </div>
              </div>
            </details>

            {error && <Alert variant="danger">{error}</Alert>}
          </div>
        )}

        <div className="mt-6 flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!prep.data || !roleArn.trim() || connect.isPending}
            onClick={() => {
              setError(null);
              connect.mutate();
            }}
          >
            {connect.isPending ? 'Connecting…' : 'Connect'}
          </Button>
        </div>
      </Card>
    </div>
  );
}
